/**
 * Image format filtering for iOS compatibility
 * iOS WebView doesn't support AVIF/WebP formats reliably
 */

const UNSUPPORTED_EXTENSIONS = ['.avif', '.webp'];
const SUPPORTED_EXTENSIONS = ['.jpg', '.jpeg', '.png'];

/**
 * Check if image URL uses an unsupported format (AVIF/WebP)
 */
export function isUnsupportedImage(url) {
  if (!url) return false;
  const lower = url.split('?')[0].toLowerCase();
  return UNSUPPORTED_EXTENSIONS.some(ext => lower.endsWith(ext));
}

/**
 * Check if image URL uses a supported format (JPG/PNG) or is likely a valid image
 */
export function isProbablyValidImage(url) {
  if (!url) return false;
  const lower = url.split('?')[0].toLowerCase();
  
  // Explicit supported extensions
  if (SUPPORTED_EXTENSIONS.some(ext => lower.endsWith(ext))) return true;
  
  // URLs without explicit extension are usually valid (CDN, dynamic images)
  // Only reject if explicitly unsupported format
  if (UNSUPPORTED_EXTENSIONS.some(ext => lower.endsWith(ext))) return false;
  
  // Accept URLs that look like images (contain image-related paths)
  if (lower.includes('/images/') || lower.includes('/img/') || 
      lower.includes('/product') || lower.includes('/media/') ||
      lower.includes('cdn.') || lower.includes('static.') ||
      lower.includes('ssl-images') || lower.includes('images-amazon')) {
    return true;
  }
  
  // Accept if it's a known image CDN
  const imageCDNs = ['cloudinary', 'imgix', 'fastly', 'akamai', 'cloudfront', 'shopify'];
  if (imageCDNs.some(cdn => lower.includes(cdn))) return true;
  
  // Default: accept URLs without blocking extension
  return true;
}

/**
 * Select the best image from preview data, preferring JPG/PNG over AVIF/WebP
 * Returns null if only unsupported formats are available
 */
export function selectBestImageFromPreview(previewData) {
  const candidates = [];

  // Collect all possible image URLs
  if (previewData.image) candidates.push(previewData.image);
  if (previewData.imageUrl) candidates.push(previewData.imageUrl);
  if (Array.isArray(previewData.images)) {
    candidates.push(...previewData.images);
  }

  console.log('📸 IMAGE CANDIDATES:', candidates);

  // 1. Prefer JPG/PNG (iOS compatible)
  const good = candidates.find(isProbablyValidImage);
  if (good) {
    console.log('✅ Selected JPG/PNG image:', good);
    return good;
  }

  // 2. If all are AVIF/WebP, return null (use placeholder)
  const allUnsupported = candidates.length > 0 && candidates.every(isUnsupportedImage);
  if (allUnsupported) {
    console.log('⚠️ ONLY UNSUPPORTED IMAGES (avif/webp) FOUND, USING PLACEHOLDER');
    return null;
  }

  // 3. Otherwise use first available (might work on web)
  const fallback = candidates[0] || null;
  if (fallback) {
    console.log('⚠️ Using fallback image (unknown format):', fallback);
  }
  return fallback;
}
